import {
  ColorQuadruplet,
  IUpdatePatternItem,
  PersistentFloat32Array,
  UpdateType,
} from '../../sharedComponents/webglUtils';
import {
  failIfValidOrNonExhaustive,
} from '../../Utils';
import {
  ChartLine,
  LineStyle,
} from './otherTypes';

// The instanced attributes for each vertex consist of a total of 14 floats:
export const numFloatsPerLineInstance = 14;

export const lineReferenceCoordValues = [
  // top left corner:
  0, 0.5,
  // bottom left corner:
  0, -0.5,
  // bottom right corner:
  1, -0.5,
  // top right corner:
  1, 0.5,
];

export const lineIndices = [
  0, 1, 3,
  1, 3, 2,
];
export const lineIndicesCount = lineIndices.length;

export interface LineInternal {
  id: string;
  x0: number;
  y0: number;
  x1: number;
  y1: number;
  dashLength: number;
  color: ColorQuadruplet;
  thickness: number;
}

export const convertToInternalLines = (input: {
    lines: ChartLine[],
    standardColor: ColorQuadruplet,
    shortDashLength: number,
    longDashLength: number,
    longDashColor: ColorQuadruplet,
    canvasLargerSide: number,
  }) => {

  const {
    lines, shortDashLength, longDashLength, canvasLargerSide,
    longDashColor, standardColor,
  } = input;

  const nextLines: Map<string, LineInternal> = new Map();
  const nextKeys: string[] = [];
  for (const line of lines) {
    let dashLength: number;
    let color: ColorQuadruplet;
    if (line.style === LineStyle.LongDash) {
      dashLength = longDashLength;
      color = longDashColor;
    } else if (line.style === LineStyle.ShortDash) {
      dashLength = shortDashLength;
      color = standardColor;
    } else if (line.style === LineStyle.Solid) {
      dashLength = canvasLargerSide;
      color = standardColor;
    } else {
      failIfValidOrNonExhaustive(line.style, 'Invalid line style ' + line.style);
      dashLength = 0;
      color = standardColor;
    }
    const id = line.id;
    const internal: LineInternal = {
      id,
      x0: line.x0,
      x1: line.x1,
      y0: line.y0,
      y1: line.y1,
      color,
      dashLength,
      thickness: line.thickness,
    };
    nextLines.set(id, internal);
    nextKeys.push(id);
  }

  return {nextLines, nextKeys};
};

export const writeToLinesBuffer = (input: {
    prevLines: Map<string, LineInternal>,
    nextLines: Map<string, LineInternal>,
    linesData: PersistentFloat32Array,
    updatePattern: IUpdatePatternItem[],
  }) => {

  const {
    prevLines, nextLines, linesData,
    updatePattern,
  } = input;

  const updatePatternLength = updatePattern.length;
  const buffer = linesData.buffer;
  linesData.length = updatePatternLength * numFloatsPerLineInstance;

  for (let i = 0; i < updatePatternLength; i += 1) {
    const pattern = updatePattern[i];
    const {key, type} = pattern;

    let initialColor: ColorQuadruplet, finalColor: ColorQuadruplet;
    if (type === UpdateType.Enter) {
      const innerLine = nextLines.get(key)!;
      initialColor = innerLine.color.slice(0) as ColorQuadruplet;
      finalColor = innerLine.color.slice(0) as ColorQuadruplet;
      initialColor[3] = 0;
    } else if (type === UpdateType.Exit) {
      const innerLine = prevLines.get(key)!;
      initialColor = innerLine.color.slice(0) as ColorQuadruplet;
      finalColor = innerLine.color.slice(0) as ColorQuadruplet;
      finalColor[3] = 0;
    } else if (type === UpdateType.Update) {
      const prevLine = prevLines.get(key)!;
      const nextLine = nextLines.get(key)!;
      initialColor = prevLine.color.slice(0) as ColorQuadruplet;
      finalColor = nextLine.color.slice(0) as ColorQuadruplet;
    } else {
      failIfValidOrNonExhaustive(type, 'Invalid update type' + type);
      // These lines will never be executed:
      initialColor = [0, 0, 0, 0];
      finalColor = [0, 0, 0, 0];
    }

    const startngIndexForThisLine = i * numFloatsPerLineInstance;

    buffer[startngIndexForThisLine + 0] = initialColor[0];
    buffer[startngIndexForThisLine + 1] = initialColor[1];
    buffer[startngIndexForThisLine + 2] = initialColor[2];
    buffer[startngIndexForThisLine + 3] = initialColor[3];
    buffer[startngIndexForThisLine + 4] = finalColor[0];
    buffer[startngIndexForThisLine + 5] = finalColor[1];
    buffer[startngIndexForThisLine + 6] = finalColor[2];
    buffer[startngIndexForThisLine + 7] = finalColor[3];

    let line: LineInternal;
    if (type === UpdateType.Enter) {
      line = nextLines.get(key)!;
    } else if (type === UpdateType.Exit) {
      line = prevLines.get(key)!;
    } else if (type === UpdateType.Update) {
      line = nextLines.get(key)!;
    } else {
      failIfValidOrNonExhaustive(type, 'Invalid update type' + type);
      line = undefined as any;
    }

    buffer[startngIndexForThisLine + 8] = line.x0;
    buffer[startngIndexForThisLine + 9] = line.y0;
    buffer[startngIndexForThisLine + 10] = line.x1;
    buffer[startngIndexForThisLine + 11] = line.y1;

    buffer[startngIndexForThisLine + 12] = line.thickness;
    buffer[startngIndexForThisLine + 13] = line.dashLength;
  }

  return {
    instanceCount: updatePatternLength,
  };

};
